"use client"

import Dinero from "dinero.js"
import { CheckIcon, HeadsetIcon } from "lucide-react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { useRouter } from "nextjs-toploader/app"
import { type HTMLAttributes, useState } from "react"
import { NeoSkeuomorphismIcon } from "@/components/misc/neo-skeuomorphism-icon"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import { config } from "@/config"
import { Entitlement, type ProductWithMetadata } from "@/lib/polar/types"
import { cn } from "@/utils/cn"
import { getAbsoluteUrl } from "@/utils/url"
import {
  WidgetCard,
  WidgetCardContent,
  WidgetCardHeader,
  WidgetCardTitle,
} from "../../(views)/(analytics)/overview/_components/widget-card"
import { useProduct } from "../hooks"
import type { TPricingPlan, TPricingPlanFeature } from "../types"
import { MoneyBackNotice } from "./money-back-notice"

Dinero.defaultCurrency = "USD"
Dinero.globalLocale = "en-US"

interface PricingPlanItemProps extends HTMLAttributes<HTMLDivElement> {
  plan: TPricingPlan
  interval: "month" | "year"
  mode: "page" | "app"
}

export function PricingPlanItem({
  plan,
  interval,
  mode,
  className,
  ...props
}: PricingPlanItemProps) {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const t = useTranslations()

  const isEnterprise = plan.entitlement === Entitlement.ENTERPRISE
  const product = useProduct(plan.entitlement, interval)
  if (!product && !isEnterprise) return null

  // Calculate Pricing
  let monthlyPrice = Dinero({ amount: product?.fixedPrice.priceAmount ?? 0 })
  if (interval === "year") monthlyPrice = monthlyPrice.divide(12)
  const monthlyPriceFormatted = isEnterprise
    ? t("Pricing.custom-pricing")
    : monthlyPrice.toFormat("$0,0.00")

  let yearlyPrice = Dinero({ amount: product?.fixedPrice.priceAmount ?? 0 })
  if (interval === "month") yearlyPrice = yearlyPrice.multiply(12)
  const yearlyPriceFormatted = yearlyPrice.toFormat("$0,0.00")

  // Handle plan click
  const handlePlanClick = () => {
    setIsLoading(true)

    const checkoutUrl = new URL(getAbsoluteUrl(`/api/checkout/${product?.metadata.slug}`))

    // Determine referral id (if set)
    const referralId =
      typeof window !== "undefined"
        ? (window as Window & { affonso_referral?: string }).affonso_referral
        : undefined
    if (referralId) {
      checkoutUrl.searchParams.set("referral_id", referralId)
    }

    router.push(checkoutUrl.toString())
  }

  return (
    <WidgetCard
      tint={plan.isPopular ? "primary" : "none"}
      containerClassName={cn(
        "p-0 @container rounded-3xl [&_>_div:first-child]:via-background!",
        plan.isPopular
          ? "border-purple-500/25 ring-purple-500/15 ring-1 ring-offset-background ring-offset-2"
          : "dark:[&_>_div:first-child]:from-foreground/[0.05] [&_>_div:first-child]:from-foreground/[0.05]",
        className,
      )}
      {...props}
    >
      <WidgetCardHeader className="min-h-[185px] flex-col! items-stretch @[22rem]:px-5 px-4 py-4">
        <WidgetCardTitle className="flex items-center gap-4">
          <NeoSkeuomorphismIcon
            icon={<plan.icon weight="fill" />}
            size="sm"
            tint={plan.isPopular ? "primary" : "none"}
          />

          <span className="font-display font-extrabold">{t(plan.name)}</span>

          {plan.isPopular && (
            <Badge
              variant="secondary"
              className="ml-auto bg-purple-400/15! px-3 py-1 font-sans text-purple-500 dark:text-purple-300"
            >
              {t("Pricing.most-powerful")}
            </Badge>
          )}
        </WidgetCardTitle>

        <div className="flex grow flex-col justify-center">
          <div>
            <span className="font-display font-extrabold text-3xl tracking-tight">
              {monthlyPriceFormatted}
            </span>
            <span className="ml-1 font-medium text-muted-foreground/80 text-xs">
              {t("Pricing.per-month")}
            </span>
          </div>

          <p className="py-1 font-medium text-muted-foreground/80 text-xs">
            {isEnterprise
              ? t("Pricing.flexible-pricing")
              : interval === "year"
                ? t("Pricing.billed-as", { amount: yearlyPriceFormatted })
                : t("Pricing.switch-cancel")}
          </p>

          <div className="pt-2 font-medium text-foreground text-sm">{t(plan.description)}</div>
        </div>
      </WidgetCardHeader>

      <WidgetCardContent
        className={cn(
          "flex h-full flex-col justify-between gap-6 border-t bg-background @[22rem]:px-5 px-4 @[22rem]:pt-5 pt-4 pb-2.5",
          plan.isPopular && "border-purple-500/25",
        )}
      >
        <ul className="flex flex-col gap-2">
          {plan.features.map((feature, featureIndex) => (
            <PricingPlanFeature key={featureIndex} feature={feature} product={product} />
          ))}
        </ul>

        <PricingPlanAction
          isEnterprise={isEnterprise}
          mode={mode}
          plan={plan}
          isLoading={isLoading}
          handlePlanClick={handlePlanClick}
        />
      </WidgetCardContent>
    </WidgetCard>
  )
}

interface PricingPlanFeatureProps {
  feature: TPricingPlanFeature
  product: ProductWithMetadata | null
}
function PricingPlanFeature({
  feature: { feature: _feature, subtitle: _subtitle, isComingSoonSeparator, isComingSoon },
  product,
}: PricingPlanFeatureProps) {
  const t = useTranslations()

  if (!_feature) return null

  const metadata = (product?.metadata as Record<string, string | number | Date>) || {}
  const feature = t(_feature, metadata)
  const subtitle = _subtitle ? t(_subtitle, metadata) : null

  return (
    <li className={cn("flex items-center gap-2.5", isComingSoon && "opacity-50")}>
      <div className="flex size-[19px] shrink-0 items-center justify-center rounded-[8px] bg-muted/60">
        <CheckIcon className="size-[13px] text-foreground" />
      </div>
      <div className="flex flex-col">
        <div className="flex w-full items-center gap-2">
          <span className="font-medium dark:font-normal">{feature}</span>
          {isComingSoon && <Badge variant="secondary">{t("Pricing.coming-soon")}</Badge>}
        </div>
        {subtitle && <span className="font-medium text-muted-foreground text-xs">{subtitle}</span>}
      </div>
    </li>
  )
}

interface PricingPlanActionProps {
  isEnterprise: boolean
  mode: "page" | "app"
  plan: TPricingPlan
  isLoading: boolean
  handlePlanClick: () => void
}

function PricingPlanAction({
  isEnterprise,
  mode,
  plan,
  isLoading,
  handlePlanClick,
}: PricingPlanActionProps) {
  const t = useTranslations()

  return (
    <div className="flex flex-col gap-2.5">
      {isEnterprise ? (
        <a
          href={config.links.calEnterprise}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={cn(
            buttonVariants({ size: "lg", variant: "secondary" }),
            "rounded-xl ring-4 ring-muted/20 dark:ring-muted/30",
          )}
        >
          <HeadsetIcon />
          {t("Pricing.book-call")}
        </a>
      ) : mode === "app" ? (
        <Button
          size="lg"
          variant={plan.isPopular ? "default" : "secondary"}
          isLoading={isLoading}
          loadingText={t("Pricing.redirecting")}
          onClick={handlePlanClick}
          className={cn(
            "rounded-xl ring-4",
            plan.isPopular
              ? "ring-foreground/10 dark:ring-foreground/20"
              : "ring-muted/20 dark:ring-muted/30",
          )}
        >
          {t("Pricing.select-plan")}
        </Button>
      ) : (
        <Link
          href="/app"
          className={cn(
            buttonVariants({
              size: "lg",
              variant: plan.isPopular ? "default" : "secondary",
            }),
            "rounded-xl ring-4",
            plan.isPopular ? "ring-foreground/20" : "ring-muted/30",
          )}
        >
          {t("Pricing.select-plan")}
        </Link>
      )}

      <MoneyBackNotice className="self-center" />
    </div>
  )
}
